// src/components/common/LoadingOverlay.js
// Loading Overlay and Common UI Components - Production Ready

import React from 'react';
import {
  Box,
  Flex,
  Spinner,
  Text,
  Progress,
  useColorModeValue,
  Center,
  VStack,
  // --- UPDATE: Moved imports from line 208 ---
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Icon,
  // --- UPDATE: Moved imports from line 273 ---
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  // --- UPDATE: Moved imports from line 324 ---
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
// --- UPDATE: Moved imports from line 219 ---
import { MdWarning, MdSearch, MdClose } from 'react-icons/md';
// --- UPDATE: Moved imports from line 274 ---
import { useState, useEffect } from 'react';

/**
 * LoadingOverlay Component
 * Displays a loading overlay over content
 */
export const LoadingOverlay = ({ 
  isLoading, 
  text = 'Loading...', 
  children,
  overlay = true,
  minHeight = '200px',
}) => {
  const bgColor = useColorModeValue('whiteAlpha.800', 'blackAlpha.800');

  if (!isLoading) {
    return <>{children}</>;
  }

  if (overlay) {
    return (
      <Box position="relative">
        {children}
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg={bgColor}
          backdropFilter="blur(2px)"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
          borderRadius="md"
        >
          <VStack spacing={4}>
            <Spinner size="xl" thickness="4px" color="blue.500" />
            {text && (
              <Text fontSize="sm" fontWeight="medium">
                {text}
              </Text>
            )}
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Center minH={minHeight}>
      <VStack spacing={4}>
        <Spinner size="xl" thickness="4px" color="blue.500" />
        {text && (
          <Text fontSize="sm" fontWeight="medium">
            {text}
          </Text>
        )}
      </VStack>
    </Center>
  );
};

/**
 * LoadingProgress Component
 * Shows a progress bar with percentage
 */
export const LoadingProgress = ({ progress = 0, text }) => {
  const progressBg = useColorModeValue('gray.100', 'navy.700');
  
  return (
    <VStack spacing={3} width="100%" p={4}>
      <Progress
        value={progress}
        size="sm"
        width="100%"
        borderRadius="full"
        bg={progressBg}
        colorScheme="blue"
        hasStripe
        isAnimated
      />
      <Flex justify="space-between" width="100%" fontSize="sm">
        {text && <Text>{text}</Text>}
        <Text fontWeight="bold">{Math.round(progress)}%</Text>
      </Flex>
    </VStack>
  );
};

/**
 * EmptyState Component
 * Displays when there's no data to show
 */
export const EmptyState = ({ title, description, icon, action }) => {
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const bgColor = useColorModeValue('gray.50', 'navy.800');

  return (
    <Center minH="300px" p={8}>
      <VStack
        spacing={4}
        bg={bgColor}
        p={8}
        borderRadius="lg"
        maxW="400px"
        textAlign="center"
      >
        {icon && (
          <Box fontSize="4xl" opacity={0.5}>
            {icon}
          </Box>
        )}
        {title && (
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            {title}
          </Text>
        )}
        {description && (
          <Text fontSize="sm" color={textColor}>
            {description}
          </Text>
        )}
        {action && <Box mt={2}>{action}</Box>}
      </VStack>
    </Center>
  );
};

/**
 * SkeletonLoader Component
 * Displays skeleton loading placeholders
 */
export const SkeletonLoader = ({ count = 3, height = '40px', spacing = 3 }) => {
  const skeletons = Array.from({ length: count }, (_, i) => i);
  
  return (
    <VStack spacing={spacing} width="100%">
      {skeletons.map(i => (
        <Box
          key={i}
          height={height}
          width="100%"
          borderRadius="md"
          bg="gray.200"
          opacity={0.6}
          animation="pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
        />
      ))}
    </VStack>
  );
};

/**
 * ConfirmDialog Component
 * Reusable confirmation dialog
 */
// --- IMPORTS WERE HERE (MOVED TO TOP) ---

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  colorScheme = 'blue',
  isLoading = false,
}) => {
  const modalBg = useColorModeValue('white', 'navy.800');

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>
          <Flex align="center" gap={2}>
            <Icon as={MdWarning} boxSize={5} color={`${colorScheme}.500`} />
            {title}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>{message}</Text>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme={colorScheme}
            mr={3}
            onClick={onConfirm}
            isLoading={isLoading}
          >
            {confirmText}
          </Button>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            {cancelText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/**
 * SearchInput Component
 * Debounced search input with clear button
 */
// --- IMPORTS WERE HERE (MOVED TO TOP) ---

export const SearchInput = ({
  value,
  onChange,
  placeholder = 'Search...',
  debounceMs = 300,
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      onChange(localValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, onChange, debounceMs]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <InputGroup>
      <InputLeftElement>
        <Icon as={MdSearch} color="gray.400" />
      </InputLeftElement>
      <Input
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
      />
      {localValue && (
        <InputRightElement>
          <IconButton
            aria-label="Clear search"
            icon={<Icon as={MdClose} />}
            size="sm"
            variant="ghost"
            onClick={() => {
              setLocalValue('');
              onChange('');
            }}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
};

/**
 * DataStatus Component
 * Shows data loading/error/empty states
 */
// --- IMPORTS WERE HERE (MOVED TO TOP) ---

export const DataStatus = ({
  isLoading,
  error,
  data,
  children,
  emptyState = {},
  loadingText = 'Loading...',
}) => {
  if (isLoading) {
    return <LoadingOverlay isLoading={true} text={loadingText} overlay={false} />;
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Error!</AlertTitle>
        <AlertDescription>
          {error.message || 'An error occurred while loading data.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        title={emptyState.title || 'No data found'}
        description={emptyState.description}
        icon={emptyState.icon}
        action={emptyState.action}
      />
    );
  }

  return <>{children}</>;
};

export default {
  LoadingOverlay,
  LoadingProgress,
  EmptyState,
  SkeletonLoader,
  ConfirmDialog,
  SearchInput,
  DataStatus,
};